import { AlertTriangle, CheckCircle2, X, XCircle } from "lucide-react";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import React from "react";

type ToastType = "success" | "error" | "warning";

interface Toast {
    id: number; 
    message: string; 
    type: ToastType;
    duration?: number;
}

interface ToastContextValue {
    toasts: Toast[];
    addToast: (message: string, type: ToastType, duration?: number) => void;
    removeToast: (id: number) => void; 
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const nextToastId = useRef(0);
    const timeoutIds = useRef(new Map<number, ReturnType<typeof setTimeout>>());

    const removeToast = useCallback((id: number) => {
        const timeoutId = timeoutIds.current.get(id);
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutIds.current.delete(id);
        }
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback((message: string, type: ToastType, duration = 7000) => {
        const id = nextToastId.current + 1;
        nextToastId.current = id;
        setToasts((prev) => [...prev, { id, message, type, duration }]);
        const timeoutId = setTimeout(() => removeToast(id), duration);
        timeoutIds.current.set(id, timeoutId);
    }, [removeToast]);

    useEffect(() => {
        return () => {
            timeoutIds.current.forEach(clearTimeout);
            timeoutIds.current.clear();
        };
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
            {children}
        </ToastContext.Provider>
    );
}

export function useToast(){
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within ToastProvider");
    }
    return context;
}


export function ToastContainer() {
    const { toasts, removeToast } = useToast();
    return (
        <div className="toast-container" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
        ))}
        </div>
    );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
    const Icon =
        toast.type === "success"
            ? CheckCircle2
            : toast.type === "warning"
                ? AlertTriangle
                : XCircle;
    const role = toast.type === "error" ? "alert" : "status";

    return (
        <div className={`toast toast--${toast.type}`} role={role}>
            <Icon className="toast__icon" size={20} aria-hidden="true" />
            <span className="toast__message">{toast.message}</span>
            <button
                type="button"
                className="toast__close"
                aria-label="Dismiss notification"
                title="Dismiss notification"
                onClick={onDismiss}
            >
                <X size={16} aria-hidden="true" />
            </button>
        </div>
    );
}
